import * as Sentry from "@sentry/vue";
import { useRoute } from "vue-router";
import axios from "axios";
import qs from "qs";
import { useAuthStore } from "@/store/auth";

declare module "axios" {
  export interface InternalAxiosRequestConfig {
    initiatedOnAuthedRoute?: boolean;
  }
}

export const baseUrl = `${location.protocol}//${location.host}`;

const AXIOS_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

const apiOptions = {
  baseURL: `${baseUrl}/api/`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  timeout: AXIOS_TIMEOUT_MS,
  timeoutMessage: "Request timed out",
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
  paramsSerializer: {
    serialize: (params: Record<string, string | number | null>): string => {
      return qs.stringify(params, { arrayFormat: "comma", skipNulls: true });
    },
  },
};
const api = axios.create(apiOptions);
api.interceptors.request.use(
  (config) => {
    config.signal = AbortSignal.timeout(AXIOS_TIMEOUT_MS);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export function useLogoutInterceptor(): void {
  const authStore = useAuthStore();
  const route = useRoute();
  api.interceptors.request.use((config) => {
    config.initiatedOnAuthedRoute = Boolean(route.meta?.requiresAuth);
    return config;
  });
  api.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      // URLs where 403 should NOT trigger logout (feature-gated endpoints)
      const noLogoutOn403Urls = [
        "/api/oauth/xandr/authorize/",
        "/api/oauth/kobler/authorize/",
        "/api/oauth/c_alt/authorize/",
        "/api/oauth/matomo/authorize/",
        "/api/oauth/omnium/authorize/", // 403 means crm feature flag disabled
        "/api/auth/invites/",
        "/api/ai-agents/", // AI agents - 403 means feature flag disabled
        "/api/ai/", // AI features - 403 means feature flag disabled
        "/api/ai-suggestions/", // AI recommendations - 403 means feature flag disabled
        "/api/ai-suggestion-tracking/", // AI recommendation tracking - 403 means feature flag disabled
        "/api/recommendations/my-tasks/", // My Tasks - 403 means feature flag/consent denied
        // Promoted AI tasks live under `/api/recommendations/AI_SUGGESTION/...`
        // and inherit the AI gate via `AIRecommendationsEnabledForAITypes`.
        // Without the carve-out, an env-switch flip would log the user
        // out on the next step PATCH / claim / retrieve.
        "/api/recommendations/AI_SUGGESTION/",
        "/api/budget-tool/", // Budget tool - 403 means feature flag disabled
        "/api/ai-visibility/", // AI visibility - 403 means feature flag disabled
        "/api/crm/", // Customers & Commerce - 403 means crm feature flag disabled
        "/api/feature-flags/consents/", // Consent actions - 403 means not a manager
        "/api/auth/business-areas/", // Business areas - 403 means not a manager in the selected org
        "/api/kpis/", // KPI hub-calculate - 403 means cross-org permission denied, not auth failure
        // AI dashboard actions: `_check_ai_dashboards_enabled` raises 403
        // when the flag is off or consent was revoked. Feature flags carry a
        // 5-minute staleTime, so the buttons can still be rendered when that
        // happens — without the carve-out, clicking Reject logs the user out
        // instead of showing the refusal in the banner.
        "-ai-changes/",
      ];
      const responseURL = error.request?.responseURL ?? "";
      // Feature gates raising `FeatureDisabled` say so in the body, so they need
      // no URL entry above and an expired session on the same URL still logs out.
      const deniedByFeatureFlag =
        error.response?.data?.code === "feature_disabled";
      const shouldLogoutOn403 =
        !deniedByFeatureFlag &&
        !noLogoutOn403Urls.some((url) => responseURL.includes(url));

      // Judge by the route at request time: in-flight 403s land after
      // auto-logout has already redirected to /auth/login (FYR-A6E)
      const initiatedOnAuthedRoute =
        error.config?.initiatedOnAuthedRoute ??
        Boolean(route.meta?.requiresAuth);

      if (
        shouldLogoutOn403 &&
        initiatedOnAuthedRoute &&
        error.response?.status === 403 &&
        authStore.isAuthed
      ) {
        authStore.setLoggedOut();
      } else if (error.response?.status === 403 && !initiatedOnAuthedRoute) {
        Sentry.captureException({
          error: error,
          message: "403 Encountered in a non-authed route",
          route: route.path,
          // Without these three, every variant of this capture collapses into
          // one Sentry group: a public dashboard calling an authed endpoint (the
          // case this capture exists for) is indistinguishable from an in-flight
          // request whose stamp went missing (FYR-1F4).
          requestUrl: responseURL,
          routeAtRequestTime:
            error.config?.initiatedOnAuthedRoute === undefined
              ? "unstamped"
              : "public",
          isAuthed: authStore.isAuthed,
        });
      }
      return Promise.reject(error);
    },
  );
}

export default api;

export interface APIError {
  message: string;
  code: string;
}
